/**
 * PrepFannedStack — fanned archive for completed PREPARATION REQUESTS
 * in the File Completed column (CLAUDE.md §20.8).
 *
 * Same MECHANIC as the completed-file archive — FannedStackBase provides
 * the hover-intent expansion, exclusive expand, touch protocol and
 * reduced-motion handling — but the PRESENTATION stays in the prep
 * requests' notebook language: collapsed entries read as a pile of cream
 * paper sheets (red margin rule, Caveat client name), NOT as copies of
 * the navy file-folder tabs. Hovering a sheet expands it into the
 * standard PrepSheetCard, identical to prep cards in every other column,
 * badges (notes count, Done x/y, TEST) included.
 *
 * Files and prep requests stack SEPARATELY: this component only ever
 * receives prep records, and the file FannedStack only ever receives
 * files (SharedPipeline keeps the two collections apart).
 */

import React from 'react';
import FannedStackBase from './FannedStackBase';
import PrepSheetCard, { INK, INK_LIGHT, MARGIN, PAPER, PAPER_EDGE } from './PrepSheetCard';
import { prepTaskProgress } from '../../utils/prepRequests';

export default function PrepFannedStack({
  preps,
  notesCountFor = () => 0,
  onPrepClick,
  emptyMessage = 'No preparation requests',
  emptyStyle,
  // App-level role gate (CLAUDE.md §30): when false the sheets drop the
  // pointer cursor + button semantics and the expand affordance. Accepts
  // a boolean OR a per-record predicate `(prep) => boolean`, because prep
  // access is per record (§26.1) — the Administration Assistant may open
  // only the requests he loaded, so one stack can mix openable and inert
  // sheets. FannedStackBase resolves it item by item.
  interactive = true,
}) {
  return (
    <FannedStackBase
      items={preps}
      idOf={(p) => p.id}
      onItemOpen={onPrepClick}
      emptyMessage={emptyMessage}
      emptyStyle={emptyStyle}
      interactive={interactive}
      renderExpanded={(prep, { onClick, interactive: itemInteractive }) => (
        /* The standard prep card, exactly as it renders elsewhere in the
           column — notes badge, Done x/y and TEST treatment included.
           Illumination is not passed: the Completed column never
           illuminates (§31.4). */
        <PrepSheetCard
          prep={prep}
          notesCount={notesCountFor(prep) || 0}
          interactive={itemInteractive}
          onClick={onClick}
        />
      )}
      renderCollapsed={(prep, { onClick, focusBind, reducedMotion, interactive: itemInteractive }) => (
        <CollapsedSheet
          prep={prep}
          reducedMotion={reducedMotion}
          interactive={itemInteractive}
          onClick={onClick}
          focusBind={focusBind}
        />
      )}
    />
  );
}

/**
 * A collapsed prep entry: a narrow strip of the same cream ruled paper the
 * full card uses — red margin rule at the same 24px, Caveat client name,
 * Kalam "Done · n/n" on the right — so the stack reads as sheets of
 * notepaper, in the notebook's own visual language. Test records keep
 * their existing dashed-coral treatment (§33.5) while collapsed.
 */
function CollapsedSheet({ prep, reducedMotion, interactive = true, onClick, focusBind }) {
  const { total } = prepTaskProgress(prep);
  const displayName = truncate(prep.clientName || '', 22);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick();
    }
  };

  const interactiveBind = interactive
    ? {
        role: 'button',
        tabIndex: 0,
        'aria-label': `Expand preparation request details for ${prep.clientName}`,
        onKeyDown: handleKeyDown,
        ...focusBind,
      }
    : {};

  return (
    <div
      onClick={onClick}
      {...interactiveBind}
      // Return-to-dashboard lookup (CLAUDE.md §39).
      data-card-type="prep"
      data-card-id={prep.id}
      style={{
        height: '40px',
        minHeight: '40px',
        // Clear the red margin rule so the name starts on the writing area,
        // exactly as it does on the full card (which pads 36px past it).
        padding: '0 12px 0 32px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '8px',
        background: PAPER,
        // The full card's red margin rule at the same 24-25px offset, so the
        // collapsed strip is recognisably the top edge of the same paper.
        backgroundImage: `linear-gradient(to right, transparent 24px, ${MARGIN} 24px, ${MARGIN} 25px, transparent 25px)`,
        borderLeft: `1px solid ${PAPER_EDGE}`,
        // Paper is square-cornered (the full card uses 2px), and the same
        // soft up-shadow the navy tabs use for stacking depth.
        borderRadius: '2px',
        boxShadow: '0 -2px 6px rgba(26, 37, 64, 0.08)',
        cursor: interactive ? 'pointer' : 'default',
        userSelect: 'none',
        // Focus ring suppressed like the navy tabs — except on test records,
        // where the outline slot carries the existing dashed-coral marker
        // (§33.5) so a collapsed test sheet stays unmistakable.
        outline: prep.isTest ? '2px dashed var(--cp-warning)' : 'none',
        ...(prep.isTest ? { outlineOffset: '-2px' } : {}),
        transition: reducedMotion ? 'none' : 'box-shadow 200ms ease-out',
      }}
    >
      <span
        style={{
          fontFamily: "'Caveat', cursive",
          fontSize: '18px',
          fontWeight: 700,
          color: INK,
          lineHeight: 1,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          flex: '1 1 auto',
        }}
      >
        {displayName}
      </span>
      <span
        style={{
          flex: '0 0 auto',
          fontFamily: "'Kalam', cursive",
          fontSize: '11px',
          fontWeight: 700,
          color: INK_LIGHT,
          whiteSpace: 'nowrap',
        }}
      >
        {total > 0 ? `Done · ${total}/${total}` : 'Done'}
      </span>
    </div>
  );
}

function truncate(text, max) {
  if (text.length <= max) return text;
  return `${text.slice(0, Math.max(0, max - 1)).trimEnd()}…`;
}
